import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../config/firebase';
import { IReportRepository, UpdateReportData, ReportFilters } from '../interfaces/IReportRepository';
import { Report } from '../../types';

export class FirebaseReportRepository implements IReportRepository {
  private readonly collectionName = 'reports';

  async create(data: Omit<Report, 'id'>, photoFile?: File): Promise<Report> {
    try {
      let photoUrl: string | undefined;

      // Upload de la photo si présente
      if (photoFile) {
        // Utiliser un timestamp pour l'ID si pas de user spécifique
        photoUrl = await this.uploadPhoto(photoFile, 'anonymous-user');
      }

      // Obtenir l'userId de l'utilisateur actuellement connecté
      const currentUser = auth?.currentUser;
      if (!currentUser) {
        throw new Error('Utilisateur non connecté');
      }

      const reportData: any = {
        title: data.title,
        description: data.description,
        location: data.location,
        latitude: data.latitude,
        longitude: data.longitude,
        userId: currentUser.uid,
        status: data.status || 'in_progress',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      // Ajouter photoUrl seulement s'il n'est pas undefined
      if (photoUrl) {
        reportData.photoUrl = photoUrl;
      }

      const docRef = await addDoc(collection(db!, this.collectionName), reportData);

      return {
        id: docRef.id,
        title: data.title,
        date: data.date || new Date().toISOString(),
        location: data.location,
        latitude: data.latitude,
        longitude: data.longitude,
        status: data.status || 'in_progress',
        description: data.description,
        photoUrl,
        userId: currentUser.uid,
      };
    } catch (error) {
      console.error('Erreur lors de la création du signalement:', error);
      throw new Error('Erreur lors de la création du signalement');
    }
  }

  async getById(id: string): Promise<Report | null> {
    try {
      const docRef = doc(db!, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.firestoreToReport(id, docSnap.data());
    } catch (error) {
      console.error('Erreur lors de la récupération du signalement:', error);
      return null;
    }
  }

  async getAll(filters?: ReportFilters): Promise<Report[]> {
    try {
      let q = query(
        collection(db!, this.collectionName),
        orderBy('createdAt', 'desc')
      );

      if (filters?.status) {
        q = query(q, where('status', '==', filters.status));
      }

      if (filters?.userId) {
        q = query(q, where('userId', '==', filters.userId));
      }

      if (filters?.limit) {
        q = query(q, limit(filters.limit));
      }

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => 
        this.firestoreToReport(doc.id, doc.data())
      );
    } catch (error) {
      console.error('Erreur lors de la récupération des signalements:', error);
      return [];
    }
  }

  async update(id: string, data: UpdateReportData): Promise<Report> {
    try {
      const docRef = doc(db!, this.collectionName, id);
      
      await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now(),
      });

      const updatedDoc = await getDoc(docRef);
      if (!updatedDoc.exists()) {
        throw new Error('Signalement non trouvé après mise à jour');
      }

      return this.firestoreToReport(id, updatedDoc.data());
    } catch (error) {
      console.error('Erreur lors de la mise à jour du signalement:', error);
      throw new Error('Erreur lors de la mise à jour du signalement');
    }
  }

  async delete(id: string): Promise<void> {
    try {
      // Récupérer d'abord le document pour supprimer la photo si elle existe
      const docRef = doc(db!, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.photoUrl) {
          await this.deletePhoto(data.photoUrl);
        }
      }

      await deleteDoc(docRef);
    } catch (error) {
      console.error('Erreur lors de la suppression du signalement:', error);
      throw new Error('Erreur lors de la suppression du signalement');
    }
  }

  async getByUserId(userId: string, limitCount?: number): Promise<Report[]> {
    try {
      let q = query(
        collection(db!, this.collectionName),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => 
        this.firestoreToReport(doc.id, doc.data())
      );
    } catch (error) {
      console.error('Erreur lors de la récupération des signalements par utilisateur:', error);
      return [];
    }
  }

  async getByStatus(status: 'in_progress' | 'done', limitCount?: number): Promise<Report[]> {
    try {
      let q = query(
        collection(db!, this.collectionName),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => 
        this.firestoreToReport(doc.id, doc.data())
      );
    } catch (error) {
      console.error('Erreur lors de la récupération des signalements par statut:', error);
      return [];
    }
  }

  async getInArea(bounds: { north: number; south: number; east: number; west: number }): Promise<Report[]> {
    try {
      // Firestore ne supporte pas les requêtes sur plusieurs champs de comparaison
      // On fait donc une requête simple et on filtre côté client
      const q = query(
        collection(db!, this.collectionName),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs
        .map(doc => this.firestoreToReport(doc.id, doc.data()))
        .filter(report => 
          report.latitude >= bounds.south &&
          report.latitude <= bounds.north &&
          report.longitude >= bounds.west &&
          report.longitude <= bounds.east
        );
    } catch (error) {
      console.error('Erreur lors de la récupération des signalements par zone:', error);
      return [];
    }
  }

  onReportsChange(callback: (reports: Report[]) => void, filters?: ReportFilters): () => void {
    let q = query(
      collection(db!, this.collectionName),
      orderBy('createdAt', 'desc')
    );

    if (filters?.status) {
      q = query(q, where('status', '==', filters.status));
    }

    if (filters?.userId) {
      q = query(q, where('userId', '==', filters.userId));
    }

    if (filters?.limit) {
      q = query(q, limit(filters.limit));
    }

    return onSnapshot(q, (querySnapshot) => {
      const reports = querySnapshot.docs.map(doc => 
        this.firestoreToReport(doc.id, doc.data())
      );
      callback(reports);
    }, (error) => {
      console.error('Erreur dans l\'écoute des changements de signalements:', error);
    });
  }

  private async uploadPhoto(file: File, userId: string): Promise<string> {
    const fileName = `${Date.now()}-${file.name}`;
    const storageRef = ref(storage!, `reports/${userId}/${fileName}`);
    
    const snapshot = await uploadBytes(storageRef, file);
    return await getDownloadURL(snapshot.ref);
  }

  private async deletePhoto(photoUrl: string): Promise<void> {
    try {
      const photoRef = ref(storage!, photoUrl);
      await deleteObject(photoRef);
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      // Ne pas faire échouer toute l'opération si la suppression de photo échoue
    }
  }

  private firestoreToReport(id: string, data: any): Report {
    return {
      id,
      title: data.title,
      date: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      location: data.location,
      latitude: data.latitude,
      longitude: data.longitude,
      status: data.status || 'in_progress',
      description: data.description,
      photoUrl: data.photoUrl,
      userId: data.userId,
    };
  }
}